/**
 * Composable: maps ISP statuses to Tailwind classes.
 * All color tokens use Chatwoot's n-* palette (no hardcoded hex).
 * border classes use ring-1 for the subtle border effect matching the design.
 */

export function getConnectionBadgeClasses(status) {
  const map = {
    online: {
      bg: 'bg-n-teal-3',
      text: 'text-n-teal-11',
      dot: 'bg-n-teal-9',
      ring: 'ring-1 ring-n-teal-6',
    },
    offline: {
      bg: 'bg-n-ruby-3',
      text: 'text-n-ruby-11',
      dot: 'bg-n-ruby-9',
      ring: 'ring-1 ring-n-ruby-6',
    },
    bloqueado: {
      bg: 'bg-n-ruby-3',
      text: 'text-n-ruby-11',
      dot: 'bg-n-ruby-9',
      ring: 'ring-1 ring-n-ruby-6',
    },
    instavel: {
      bg: 'bg-n-amber-3',
      text: 'text-n-amber-11',
      dot: 'bg-n-amber-9',
      ring: 'ring-1 ring-n-amber-6',
    },
  };
  return map[status] || map.offline;
}

export function getFinancialBadgeClasses(status) {
  const map = {
    em_dia: {
      bg: 'bg-n-teal-3',
      text: 'text-n-teal-11',
      dot: 'bg-n-teal-9',
      ring: 'ring-1 ring-n-teal-6',
    },
    atrasado: {
      bg: 'bg-n-amber-3',
      text: 'text-n-amber-11',
      dot: 'bg-n-amber-9',
      ring: 'ring-1 ring-n-amber-6',
    },
    bloqueado: {
      bg: 'bg-n-ruby-3',
      text: 'text-n-ruby-11',
      dot: 'bg-n-ruby-9',
      ring: 'ring-1 ring-n-ruby-6',
    },
    negociando: {
      bg: 'bg-n-blue-3',
      text: 'text-n-blue-11',
      dot: 'bg-n-blue-9',
      ring: 'ring-1 ring-n-blue-6',
    },
  };
  return map[status] || map.em_dia;
}

export function getMotivoBadgeClasses(motivo) {
  const map = {
    tecnico: {
      bg: 'bg-n-blue-3',
      text: 'text-n-blue-11',
      dot: 'bg-n-blue-9',
      ring: 'ring-1 ring-n-blue-6',
    },
    financeiro: {
      bg: 'bg-n-amber-3',
      text: 'text-n-amber-11',
      dot: 'bg-n-amber-9',
      ring: 'ring-1 ring-n-amber-6',
    },
  };
  return (
    map[motivo] || {
      bg: 'bg-n-slate-3',
      text: 'text-n-slate-11',
      dot: 'bg-n-slate-9',
      ring: 'ring-1 ring-n-slate-6',
    }
  );
}

export function getSignalInfo(dBm) {
  if (dBm == null || dBm === '') {
    return {
      label: '—',
      qualityLabel: 'Sem sinal',
      color: 'bg-n-slate-6',
      percent: 0,
    };
  }
  const val = Number(dBm);
  if (val >= -15) {
    return {
      label: `${val} dBm`,
      qualityLabel: 'Excelente',
      color: 'bg-n-teal-9',
      percent: 100,
    };
  }
  if (val >= -20) {
    return {
      label: `${val} dBm`,
      qualityLabel: 'Bom',
      color: 'bg-n-teal-9',
      percent: 80,
    };
  }
  if (val >= -25) {
    return {
      label: `${val} dBm`,
      qualityLabel: 'Regular',
      color: 'bg-n-amber-9',
      percent: 55,
    };
  }
  if (val >= -28) {
    return {
      label: `${val} dBm`,
      qualityLabel: 'Ruim',
      color: 'bg-n-amber-9',
      percent: 35,
    };
  }
  return {
    label: `${val} dBm`,
    qualityLabel: 'Critico',
    color: 'bg-n-ruby-9',
    percent: 15,
  };
}

// GK-HUB: Sidebar CSAT — rating helpers
export function getCsatRatingInfo(rating) {
  const map = {
    5: {
      label: 'Excelente',
      text: 'text-n-teal-11',
      bg: 'bg-n-teal-3',
      star: 'text-n-teal-9',
    },
    4: {
      label: 'Bom',
      text: 'text-n-blue-11',
      bg: 'bg-n-blue-3',
      star: 'text-n-blue-9',
    },
    3: {
      label: 'Regular',
      text: 'text-n-amber-11',
      bg: 'bg-n-amber-3',
      star: 'text-n-amber-9',
    },
    2: {
      label: 'Ruim',
      text: 'text-n-ruby-11',
      bg: 'bg-n-ruby-3',
      star: 'text-n-ruby-9',
    },
    1: {
      label: 'Péssimo',
      text: 'text-n-ruby-11',
      bg: 'bg-n-ruby-3',
      star: 'text-n-ruby-9',
    },
  };
  return (
    map[rating] || {
      label: '—',
      text: 'text-n-slate-11',
      bg: 'bg-n-slate-3',
      star: 'text-n-slate-9',
    }
  );
}

export function renderStars(rating) {
  return '★'.repeat(rating) + '☆'.repeat(5 - rating);
}
// GK-HUB: end Sidebar CSAT

export function getFaturaStatusClasses(status) {
  const map = {
    pago: { text: 'text-n-teal-11', dot: 'text-n-teal-9' },
    paga: { text: 'text-n-teal-11', dot: 'text-n-teal-9' },
    pendente: { text: 'text-n-amber-11', dot: 'text-n-amber-9' },
    aberta: { text: 'text-n-amber-11', dot: 'text-n-amber-9' },
    atrasado: { text: 'text-n-ruby-11', dot: 'text-n-ruby-9' },
    vencido: { text: 'text-n-ruby-11', dot: 'text-n-ruby-9' },
    vencida: { text: 'text-n-ruby-11', dot: 'text-n-ruby-9' },
  };
  return map[status] || { text: 'text-n-slate-11', dot: 'text-n-slate-9' };
}
